import { Link, Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { isStaffRole } from "@lms/backend/lib/auth/post-login-redirect";
import { InstructorCourses } from "@/components/instructor/instructor-courses";
import { PageShell } from "@/components/ui";
import { useAuthStore } from "@/stores/authStore";
import { fetchStaffCourses, fetchTenantMembers } from "@/services/api/instructor";

export function TenantInstructorCoursesPage() {
  const { tenantSlug = "" } = useParams<{ tenantSlug: string }>();
  const t = useTranslations("instructor");
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isTenantAdmin = user?.role === "tenant_admin";

  const coursesQuery = useQuery({
    queryKey: ["instructor-courses", tenantSlug],
    queryFn: fetchStaffCourses,
    enabled: isAuthenticated && isStaffRole(user?.role),
  });

  const membersQuery = useQuery({
    queryKey: ["tenant-members", tenantSlug],
    queryFn: fetchTenantMembers,
    enabled: isAuthenticated && isTenantAdmin,
  });

  if (!isAuthenticated) {
    return <Navigate to={`/t/${tenantSlug}/login`} replace />;
  }

  if (!isStaffRole(user?.role)) {
    return <Navigate to={`/t/${tenantSlug}/dashboard`} replace />;
  }

  if (coursesQuery.isLoading) {
    return (
      <PageShell data-testid="instructor-courses-loading">
        <p className="text-sm text-[var(--text-muted)]">{t("loading")}</p>
      </PageShell>
    );
  }

  const instructorOptions =
    isTenantAdmin && membersQuery.data
      ? membersQuery.data
          .filter((member) => member.role === "instructor" || member.role === "tenant_admin")
          .map((member) => ({
            userId: member.userId,
            name: member.name,
            email: member.email,
          }))
      : [];

  return (
    // The design system's default app measure: rows now carry status, students,
    // last-updated and five actions, which 4xl wrapped onto three lines.
    <PageShell data-testid="instructor-courses">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t("coursesTitle")}</h1>
        <Link to={`/t/${tenantSlug}/instructor`} className="text-sm text-[var(--text-muted)]">
          {t("backToInstructor")}
        </Link>
      </div>
      <InstructorCourses
        tenantSlug={tenantSlug}
        initialCourses={coursesQuery.data ?? []}
        isTenantAdmin={isTenantAdmin}
        instructorOptions={instructorOptions}
      />
    </PageShell>
  );
}
